import React, { useState, useMemo, useEffect } from "react";
import { connect } from "react-redux";

import { LocalDate } from "js-joda";
import { bindActionCreators } from "redux";

import { FilterSection, FilterItem, FilterItemField, PickerInput, DataColumnDef, DateRangeInput, CalendarInterval } from "@plex/react-components";

import { IKeyData } from "infrastructure";
import { arrayHelpers } from "infrastructure/helpers";
import { useLocalizedValues, useLocalizerWithFallback } from "ui/hooks";
import { IAccountsList, IAccountSearchCriterion, IAccount, IAccountsState, accountActions } from "ui/screens/security/models";
import { securityMessages } from "ui/screens/security/security.localization";

import { roleMembersHistoryActions } from "../RoleMembersHistory.actions";
import { IRoleMembersHistoryState, IRoleMembersHistoryFilter } from "../RoleMembersHistory.state";

export interface IRoleMembersHistoryFilterProps {
  accounts?: IAccountsList;
  filter?: IRoleMembersHistoryFilter;
  tenantId: string;
  applyFilter: (filter: IRoleMembersHistoryFilter) => void;
  loadAccounts: (criterion: IKeyData<IAccountSearchCriterion>) => void;
}

const accountPicker = "account-picker";
const updatedDateInput = "updated-date-input";

export const RoleMembersHistoryFilter: React.FC<IRoleMembersHistoryFilterProps> = ({ applyFilter, filter, tenantId, accounts, loadAccounts }) => {
  const localizedValues = useLocalizedValues(() => ({
    account: securityMessages.filter("account"),
    updatedDate: securityMessages.filter("updatedDate")
  }));

  const localizerWithFallback = useLocalizerWithFallback();

  const [state, setState] = useState<{
    criteria: IRoleMembersHistoryFilter;
    pinned: boolean;
  }>({
    criteria: {},
    pinned: false
  });

  const onPickerSelect = <T, P extends keyof IRoleMembersHistoryFilter>(rows: T[], name: P) => {
    const selected = rows[0];
    setState(prev => ({
      ...prev,
      criteria: { ...prev.criteria, [name]: selected }
    }));
  };

  const onDateRangeChange = r => {
    setState(prev => ({
      ...prev,
      criteria: { ...prev.criteria, dateRange: r ? { dateFrom: r?.start().toString(), dateTo: r?.end().toString() } : undefined }
    }));
  };

  const selectedAccount = useMemo(() => arrayHelpers.createFrom(state.criteria.account), [state.criteria.account]);

  useEffect(() => {
    setState(prev => ({
      ...prev,
      criteria: { ...filter }
    }));
  }, [filter]);

  return (
    <FilterSection
      onSearch={() => {
        applyFilter({ ...state.criteria });
      }}
      pinned={state.pinned}
      onPin={pinned => {
        setState(prevState => ({ ...prevState, pinned }));
      }}
    >
      <FilterItem id={updatedDateInput} labelText={localizedValues.updatedDate}>
        <FilterItemField value={state.criteria.dateRange}>
          <DateRangeInput
            range={state.criteria.dateRange && CalendarInterval.of(LocalDate.parse(state.criteria.dateRange.dateFrom), LocalDate.parse(state.criteria.dateRange.dateTo))}
            onRangeChange={r => onDateRangeChange(r)}
          />
        </FilterItemField>
      </FilterItem>
      <FilterItem id={accountPicker} labelText={localizedValues.account}>
        <FilterItemField value={state.criteria.account}>
          <PickerInput
            dialogTitle={localizedValues.account}
            selected={selectedAccount}
            errorMessage={localizerWithFallback(accounts?.notification?.content[0]?.message?.messageId)}
            onSelectionChanged={rows => onPickerSelect(rows, "account")}
            keySelector={(row: IAccount) => row.accountId}
            displaySelector={(row: IAccount) => row.accountName}
            data={accounts?.items}
            onSearch={(query, cols) => loadAccounts({ id: accountPicker, data: { query, searchedColumns: cols, tenantId: tenantId } })}
          >
            <DataColumnDef<IAccount> id="accountName" title={localizedValues.account} valueSelector={row => row.accountName} searchable>
              {row => row.accountName}
            </DataColumnDef>
          </PickerInput>
        </FilterItemField>
      </FilterItem>
    </FilterSection>
  );
};

export default connect(
  (state: { roleMembersHistory: IRoleMembersHistoryState; accounts: IAccountsState }) => ({
    accounts: state.accounts[accountPicker],
    filter: state.roleMembersHistory.filter,
    tenantId: state.roleMembersHistory.initData.tenantId
  }),
  dispatch => {
    return bindActionCreators(
      {
        applyFilter: roleMembersHistoryActions.applyFilter,
        loadAccounts: accountActions.loadAccounts
      },
      dispatch
    );
  }
)(RoleMembersHistoryFilter);
